/**
 * The cycle's state actions in the workspace head (f10.3), driven by f10.1's
 * `available_transitions`. The server lists what the caller may take now and
 * still enforces every edge; nothing here decides legality.
 *
 * The lock is f12.3's `FREEZE` action, offered to Finance only: it opens the
 * Lock dialog (preview, blockers, snapshot) and is never a generic transition.
 * A reopen asks for a required reason, which the server stores and audits.
 *
 * The submission is f13.2's `SUBMIT` action (Finance, Frozen): one button and
 * its confirmation, mounted outside the edge list so its outcome toast
 * survives the refetch that removes the offer.
 */

import { useState } from 'react';
import { RotateCcw, Send, Unlock } from 'lucide-react';

import { ApiError, type Cycle } from '@/api/client';
import { useCycleReleaseReadiness, useReopenCycle, useTransitionCycle } from '@/api/queries';
import { DialogField, Modal } from '@/routes/reference-data/Modal';

import { SubmitCycleButton } from '@/features/cycle-submission/SubmitCycleButton';

import { LockCycleButton } from './freeze/LockCycleDialog';
import { ReleaseDialog } from './steps/release/ReleaseDialog';

/** f10.9: the page-head release opens the same dialog as step 6, on fresh readiness. */
function PageHeadRelease({ cycle, onClose }: { cycle: Cycle; onClose: () => void }) {
  const readiness = useCycleReleaseReadiness(cycle.id);
  if (!readiness.data) return null;
  return (
    <ReleaseDialog
      cycleId={cycle.id}
      cycleVersion={cycle.version}
      readiness={readiness.data}
      onClose={onClose}
    />
  );
}

const TRANSITION_LABELS: Readonly<Record<string, string>> = {
  PREPARED: 'Mark prepared',
  OPEN_FOR_INPUT: 'Open for input',
  IN_REVIEW: 'Start review',
};

function failureMessage(error: unknown): string | null {
  if (error instanceof ApiError && error.reason === 'CYCLE_VERSION_CONFLICT') {
    return 'This cycle changed — the latest state is shown.';
  }
  return error instanceof Error ? error.message : null;
}

export function CycleStateActions({ cycle }: { cycle: Cycle }) {
  const transition = useTransitionCycle(cycle.id);
  const reopen = useReopenCycle(cycle.id);
  const [reopening, setReopening] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [reason, setReason] = useState('');

  const move = (target: string) =>
    transition.mutate({ target_state: target, expected_version: cycle.version });

  const reasonProblem =
    reopen.error instanceof ApiError
      ? reopen.error.errors.find((item) => item.key === 'reason')?.message
      : undefined;

  return (
    <>
      {cycle.available_transitions.map((edge) => {
        const key = `${edge.action}-${edge.target_state}`;
        if (edge.action === 'FREEZE') return <LockCycleButton key={key} cycle={cycle} />;
        /* f13.2: rendered once below, never as a generic transition. */
        if (edge.action === 'SUBMIT') return null;
        if (edge.action === 'REOPEN') {
          return (
            <button key={key} type="button" className="btn btn-secondary" onClick={() => setReopening(true)}>
              <Unlock aria-hidden="true" /> Reopen for review
            </button>
          );
        }
        if (edge.action === 'RELEASE') {
          return (
            <button key={key} type="button" className="btn btn-primary" onClick={() => setReleasing(true)}>
              <Send aria-hidden="true" /> Release to managers
            </button>
          );
        }
        /* f12.3: FROZEN is reached only through the FREEZE action above. */
        if (edge.target_state === 'FROZEN') return null;
        return (
          <button
            key={key}
            type="button"
            className="btn btn-secondary"
            disabled={transition.isPending}
            onClick={() => move(edge.target_state)}
          >
            <RotateCcw aria-hidden="true" />
            {TRANSITION_LABELS[edge.target_state] ?? edge.target_state}
          </button>
        );
      })}

      {!reopening && transition.error && (
        <span className="hint cycle-action-error" role="alert">
          {failureMessage(transition.error)}
        </span>
      )}

      <SubmitCycleButton cycle={cycle} />

      {releasing && <PageHeadRelease cycle={cycle} onClose={() => setReleasing(false)} />}

      {reopening && (
        <Modal
          title="Reopen for review"
          subtitle={`${cycle.derived.label} · cycle ${cycle.code}`}
          onClose={() => setReopening(false)}
          onSubmit={() =>
            reopen.mutate(
              { reason, expected_version: cycle.version },
              { onSuccess: () => setReopening(false) },
            )
          }
          busy={reopen.isPending}
          submitDisabled={!reason.trim()}
          submitLabel="Reopen"
          submitIcon={<Unlock aria-hidden="true" />}
          narrow
          layout="plain"
          message={reasonProblem ? null : failureMessage(reopen.error)}
        >
          <DialogField label="Reason" htmlFor="cycle-reopen-reason" problem={reasonProblem}>
            <textarea
              id="cycle-reopen-reason"
              rows={4}
              maxLength={2000}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </DialogField>
        </Modal>
      )}
    </>
  );
}
